// DndProvider — @dnd-kit/core 기반 드래그앤드롭 컨텍스트.
// 역할:
//   - PointerSensor(PC) + TouchSensor(모바일 250ms 딜레이) + KeyboardSensor 등록
//   - onDragEnd: active(드래그 대상) · over(드롭 대상) 분석 → moveGroup / updateItem mutation
//   - onDragOver: 트리 노드 hover 800ms → expandGroup 콜백 호출
//   - U45 멀티 드래그: onDragStart에서 active item이 선택 집합에 포함되면 multi 모드 진입.
//     onDragEnd에서 selectedIds 전체에 group_id 일괄 변경(Promise.all).
// 에러 처리:
//   - group_cycle                → "자신의 자손으로 옮길 수 없습니다"
//   - group_depth_exceeded       → "그룹 깊이가 5단을 초과합니다"
//   - group_kind_mismatch        → "다른 카테고리의 그룹으로 옮길 수 없습니다"

import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  closestCenter,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { useGroupTreeMutations } from '../hooks/useGroupsTree';
import { useItemMutations } from '../hooks/useItems';
import { useToast } from './Toast';
import { isApiError } from '../api/client';
import { dndErrorMessage, type DndDraggableData, type DndDroppableData } from '../utils/dndHelpers';
import { MultiDragPreview } from './MultiDragPreview';

// ─── DnD 확장 컨텍스트 (트리 펼침 + 멀티 드래그용) ──────────────

interface DndExtContext {
  /** 드래그 중인 아이템/그룹 ID (overlay용) */
  activeId: string | null;
  /** 현재 멀티 드래그 중인 아이템 id 수 (MultiDragPreview 표시용) */
  multiDragCount: number;
  /** hover 800ms 후 자동 펼침할 그룹 ID 등록 콜백 */
  registerExpandCallback: (cb: (groupId: number) => void) => void;
  /**
   * 현재 선택된 id 집합을 DndProvider에 등록한다.
   * Explorer 등에서 useMultiSelect.selected가 변경될 때 호출.
   * DndProvider는 onDragStart에서 이 집합을 참조해 멀티 모드 여부를 판단한다.
   */
  registerSelectedIds: (ids: Set<number>) => void;
  /** 드래그 후 선택 해제 콜백 등록 (드롭 완료 후 clear() 호출용) */
  registerClearCallback: (cb: () => void) => void;
}

const DndExtCtx = createContext<DndExtContext>({
  activeId: null,
  multiDragCount: 0,
  registerExpandCallback: () => undefined,
  registerSelectedIds: () => undefined,
  registerClearCallback: () => undefined,
});

export function useDndExt(): DndExtContext {
  return useContext(DndExtCtx);
}

// ─── DndProvider ──────────────────────────────────────────────

interface DndProviderProps {
  children: ReactNode;
}

export function DndProvider({ children }: DndProviderProps) {
  const toast = useToast();
  const { moveGroup } = useGroupTreeMutations();
  const { updateItem } = useItemMutations();

  // 드래그 중 오버레이 표시용 activeId
  const [activeId, setActiveId] = useState<string | null>(null);
  // 멀티 드래그 카운트 (0 = 단일 드래그)
  const [multiDragCount, setMultiDragCount] = useState<number>(0);

  // hover 800ms 자동 펼침 — expandCallback은 GroupTreeNode 등에서 등록
  const expandCallbackRef = useRef<((groupId: number) => void) | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastOverIdRef = useRef<string | null>(null);

  // U45: 현재 선택된 id 집합 — Explorer에서 주입
  const selectedIdsRef = useRef<Set<number>>(new Set());
  // U45: 드롭 후 선택 해제 콜백
  const clearCallbackRef = useRef<(() => void) | null>(null);
  // U45: 현재 멀티 드래그 대상 id 배열 (handleDragStart → handleDragEnd 전달용)
  const multiDragIdsRef = useRef<number[]>([]);

  const registerExpandCallback = useCallback((cb: (groupId: number) => void) => {
    expandCallbackRef.current = cb;
  }, []);

  // U45: 선택 집합 등록 — Explorer에서 useEffect로 매 렌더 후 호출
  const registerSelectedIds = useCallback((ids: Set<number>) => {
    selectedIdsRef.current = ids;
  }, []);

  // U45: 선택 해제 콜백 등록
  const registerClearCallback = useCallback((cb: () => void) => {
    clearCallbackRef.current = cb;
  }, []);

  // ─── Sensors ─────────────────────────────────────────────

  const sensors = useSensors(
    // 마우스·터치패드: 5px 이동 후 드래그 시작 (클릭 의도와 구분)
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    // 모바일 터치: 250ms 길게 누름 후 드래그 시작 (일반 탭과 구분)
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    // 키보드: 접근성 지원 (dnd-kit 기본)
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // ─── 이벤트 핸들러 ───────────────────────────────────────

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));

    // U45: active item이 선택 집합에 포함되어 있으면 multi 모드
    const activeData = event.active.data?.current as DndDraggableData | undefined;
    if (activeData?.type === 'item' && selectedIdsRef.current.size >= 2) {
      const isPartOfSelection = selectedIdsRef.current.has(activeData.itemId);
      if (isPartOfSelection) {
        const idsArray = Array.from(selectedIdsRef.current);
        multiDragIdsRef.current = idsArray;
        setMultiDragCount(idsArray.length);
        return;
      }
    }
    // 단일 드래그 — 멀티 드래그 상태 초기화
    multiDragIdsRef.current = [];
    setMultiDragCount(0);
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const overId = event.over ? String(event.over.id) : null;

    // 동일 over 대상 유지 시 타이머 재시작 안 함
    if (overId === lastOverIdRef.current) return;

    // 이전 타이머 취소
    if (hoverTimerRef.current !== null) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }

    lastOverIdRef.current = overId;

    if (!overId || !expandCallbackRef.current) return;

    const overData = event.over?.data?.current as DndDroppableData | undefined;
    if (overData?.type !== 'group') return;

    // 800ms 후 트리 노드 자동 펼침
    const groupId = overData.groupId;
    hoverTimerRef.current = setTimeout(() => {
      expandCallbackRef.current?.(groupId);
      hoverTimerRef.current = null;
    }, 800);
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      // 타이머 정리
      if (hoverTimerRef.current !== null) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
      lastOverIdRef.current = null;
      setActiveId(null);
      // multiDragCount는 ref 기반 multiDragIdsRef로 판단하므로 여기서 미리 저장
      const prevMultiCount = multiDragIdsRef.current.length;
      setMultiDragCount(0);

      const { active, over } = event;
      if (!over) return;
      if (active.id === over.id) return;

      const activeData = active.data?.current as DndDraggableData | undefined;
      const overData = over.data?.current as DndDroppableData | undefined;

      if (!activeData || !overData) return;

      try {
        // ── 그룹 → 그룹 이동 ─────────────────────────────
        if (activeData.type === 'group' && overData.type === 'group') {
          await moveGroup.mutateAsync({
            id: activeData.groupId,
            body: {
              new_parent_id: overData.groupId,
            },
          });
          return;
        }

        // ── 그룹 → 루트(null) 이동 ────────────────────────
        if (activeData.type === 'group' && overData.type === 'root') {
          await moveGroup.mutateAsync({
            id: activeData.groupId,
            body: {
              new_parent_id: null,
            },
          });
          return;
        }

        // ── 항목 → 그룹 이동 ──────────────────────────────
        if (activeData.type === 'item' && overData.type === 'group') {
          const targetGroupId = overData.groupId;

          // U45: 멀티 드래그 — 선택된 모든 항목 일괄 이동
          if (prevMultiCount >= 2 && multiDragIdsRef.current.length >= 2) {
            const idsToMove = multiDragIdsRef.current;
            multiDragIdsRef.current = [];

            const results = await Promise.allSettled(
              idsToMove.map((id) =>
                updateItem.mutateAsync({ id, patch: { group_id: targetGroupId } })
              )
            );

            const failedCount = results.filter((r) => r.status === 'rejected').length;
            const succeededCount = idsToMove.length - failedCount;

            if (failedCount > 0) {
              toast.error(`${failedCount}개 이동 실패 (${succeededCount}개 성공)`);
            } else {
              toast.success(`${succeededCount}개 항목 이동 완료`);
            }

            // 드롭 완료 후 선택 해제
            clearCallbackRef.current?.();
            return;
          }

          // 단일 항목 드래그 (기존 동작 유지)
          await updateItem.mutateAsync({
            id: activeData.itemId,
            patch: { group_id: targetGroupId },
          });
          return;
        }
      } catch (err) {
        const message = isApiError(err)
          ? dndErrorMessage(err.error)
          : '이동에 실패했습니다';
        toast.error(message);
      }
    },
    [moveGroup, updateItem, toast],
  );

  const handleDragCancel = useCallback(() => {
    if (hoverTimerRef.current !== null) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    lastOverIdRef.current = null;
    setActiveId(null);
  }, []);

  return (
    <DndExtCtx.Provider value={{
      activeId,
      multiDragCount,
      registerExpandCallback,
      registerSelectedIds,
      registerClearCallback,
    }}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {children}
        {/* 드래그 중 반투명 오버레이 — 시각 피드백 */}
        <DragOverlay>
          {activeId !== null && (
            multiDragCount >= 2
              // U45: 멀티 드래그 시 MultiDragPreview 렌더
              ? <MultiDragPreview count={multiDragCount} />
              : (
                <div className="dnd-overlay">
                  <span className="dnd-overlay__label">
                    {activeId.startsWith('group-') ? '그룹' : '항목'} 이동 중…
                  </span>
                </div>
              )
          )}
        </DragOverlay>
      </DndContext>
    </DndExtCtx.Provider>
  );
}
